import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { de } from './locales/de'
import { en } from './locales/en'
import { kalendertag } from './lib/dates'

export type Lang = 'de' | 'en'

const DICTS = { de, en } as const
const STORAGE_KEY = 'staysuite.lang'

/** Dotted key into the dictionary, e.g. `booking.arrival`. */
type Leaves<T, P extends string = ''> = {
  [K in keyof T & string]: T[K] extends string
    ? `${P}${K}`
    : Leaves<T[K], `${P}${K}.`>
}[keyof T & string]

export type TKey = Leaves<typeof de>

function lookup(dict: unknown, key: string): string | undefined {
  const value = key.split('.').reduce<unknown>(
    (acc, part) => (acc && typeof acc === 'object' ? (acc as Record<string, unknown>)[part] : undefined),
    dict,
  )
  return typeof value === 'string' ? value : undefined
}

function detectLang(): Lang {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored === 'de' || stored === 'en') return stored
  return navigator.language.toLowerCase().startsWith('de') ? 'de' : 'en'
}

interface I18nValue {
  lang: Lang
  setLang: (l: Lang) => void
  /** `t('booking.minNights', { n: 3 })` — `{name}` placeholders are substituted. */
  t: (key: TKey, vars?: Record<string, string | number>) => string
  formatMoney: (amount: number, currency?: string) => string
  formatDate: (iso: string, opts?: Intl.DateTimeFormatOptions) => string
  /** Picks the right language out of a `{ de: …, en: … }` json column. */
  pick: (value: Record<string, string> | null | undefined, fallback?: string) => string
}

const Ctx = createContext<I18nValue | null>(null)

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(detectLang)

  const setLang = useCallback((l: Lang) => {
    setLangState(l)
    localStorage.setItem(STORAGE_KEY, l)
    document.documentElement.lang = l
  }, [])

  const value = useMemo<I18nValue>(() => {
    const locale = lang === 'de' ? 'de-AT' : 'en-GB'
    return {
      lang,
      setLang,
      t: (key, vars) => {
        const raw = lookup(DICTS[lang], key) ?? lookup(DICTS.de, key) ?? key
        if (!vars) return raw
        return raw.replace(/\{(\w+)\}/g, (m, name: string) =>
          name in vars ? String(vars[name]) : m,
        )
      },
      formatMoney: (amount, currency = 'EUR') =>
        new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount),
      formatDate: (iso, opts) => {
        // `kalendertag` statt `new Date(iso + 'T00:00:00')`: bekommt die Funktion
        // einen vollen Zeitstempel, riss der alte Weg die ganze Ansicht in die
        // Fehlergrenze (TestFlight 1.0(26), 18.08.2026 — Begründung in lib/dates.ts).
        const d = kalendertag(iso)
        if (!d) return ''
        return new Intl.DateTimeFormat(locale,
          opts ?? { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d)
      },
      pick: (v, fallback = '') => v?.[lang] ?? v?.de ?? v?.en ?? fallback,
    }
  }, [lang, setLang])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useI18n(): I18nValue {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>')
  return ctx
}
